import { cn } from "@/lib/utils";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, IconNames, Integration, Shop } from "@/types/v2";
import { createSubdomainUrl } from "@/lib/createSubdomainUrl";
import { DebugInfo } from "@/components/Dashboard/components/Card/Card.DebugInfo";
import {
  getCardSection,
  generateCardDetailsUrl,
  isValidCardId,
} from "@/lib/cards";
import { useFeatures } from "@/hooks/useFeatures";
import { Icon } from "@/components/Common/Icon";

export interface CardHeaderProps {
  cardData: Card;
  subdomain?: string;
  integration?: Integration;
  shop: Shop;
}

/**
 * Hover affordance for the RO number. Only LinkedLabel actually navigates
 * anywhere (an external RO page) — Unlinked/ManualLabel have no href or
 * onClick of their own, so painting a "this is a link" cue on them would be
 * a false promise.
 */
const RO_LABEL_HOVER = "underline-offset-2 hover:text-primary hover:underline";

interface UnlinkedLabelProps {
  primaryLabel: string | undefined;
}

const UnlinkedLabel = ({ primaryLabel }: UnlinkedLabelProps) => (
  <button className="h-auto cursor-grab overflow-x-visible whitespace-nowrap text-left">
    <p>
      <span className="card-text text-[18px] font-bold leading-[100%]">
        {primaryLabel}
      </span>
    </p>
  </button>
);

interface LinkedLabelProps {
  shopId: string;
  subdomain: string;
  cardId: string;
  primaryLabel: string | undefined;
  integration: Integration;
}

const LinkedLabel = ({
  shopId,
  subdomain,
  cardId,
  primaryLabel,
  integration,
}: LinkedLabelProps) => (
  <a
    className={cn(
      "card-text h-auto w-full overflow-hidden whitespace-nowrap text-[18px] font-bold leading-[100%]",
      RO_LABEL_HOVER
    )}
    rel="noreferrer"
    target="_blank"
    onClick={(e) => e.stopPropagation()}
    href={
      integration.integration === "tekmetric"
        ? `${integration.linkUrl}/admin/shop/${shopId}/repair-orders/${cardId}/estimate`
        : integration.integration === "shopware"
          ? `${createSubdomainUrl(subdomain, integration.linkUrl)}/work_orders/${cardId}`
          : ""
    }
  >
    {primaryLabel}
  </a>
);

interface ManualLabelProps {
  primaryLabel: string | undefined;
}

const ManualLabel = ({ primaryLabel }: ManualLabelProps) => (
  <div className="relative w-full">
    <h3 className="card-text h-auto w-full overflow-hidden whitespace-nowrap text-[18px] font-bold leading-[100%]">
      {primaryLabel}
    </h3>
  </div>
);

export const CardHeader = ({
  cardData,
  subdomain,
  integration,
  shop,
}: CardHeaderProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const primaryLabel = useMemo(
    () => getCardSection(cardData, shop, "primaryField"),
    [cardData, shop]
  );
  const { features } = useFeatures(shop);

  const showWaiterIcon = useMemo(() => {
    return (
      cardData.isWaiter && shop?.cardConfig?.cardContents?.waiterIconEnabled
    );
  }, [shop, cardData]);

  const showDuplicateIcon = !!cardData.duplicatedFrom?.cardId;

  const handleDuplicateIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const sourceCardId = cardData.duplicatedFrom?.cardId;
    if (!sourceCardId || !isValidCardId(sourceCardId)) return;

    const url = generateCardDetailsUrl(shop.id, sourceCardId);
    if (event.ctrlKey || event.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <div className="min-h-4 pl-0" onDoubleClick={() => setOpen(true)}>
      <DebugInfo
        cardData={cardData}
        containers={shop?.containers ?? []}
        open={open}
        setOpen={setOpen}
        shop={shop}
        firestoreId={shop?.id}
      />

      <div className="relative w-full pl-1">
        {showWaiterIcon && (
          <Icon
            name={"waiter"}
            className={"absolute left-0.5 top-1"}
            size={14}
          />
        )}
        {showDuplicateIcon && (
          <button
            type="button"
            aria-label="Duplicated card"
            onClick={handleDuplicateIconClick}
            onMouseDown={(e) => e.stopPropagation()}
            className={cn(
              "absolute top-1 cursor-pointer",
              showWaiterIcon ? "left-3.5" : "left-0.5"
            )}
          >
            <Icon name={IconNames.duplicate} size={14} />
          </button>
        )}
        <h3
          className={cn(
            "h-auto overflow-hidden whitespace-nowrap",
            showWaiterIcon && showDuplicateIcon && "pl-5",
            (showWaiterIcon || showDuplicateIcon) &&
              !(showWaiterIcon && showDuplicateIcon) &&
              "pl-3"
          )}
        >
          {features.isManualCardOnlyEnabled ? (
            <ManualLabel primaryLabel={primaryLabel} />
          ) : features.isCardPrimaryLinkEnabled &&
            shop.id &&
            integration &&
            cardData.source !== "manual" ? (
            <LinkedLabel
              cardId={cardData.id}
              subdomain={subdomain ?? ""}
              shopId={shop.shopId}
              primaryLabel={primaryLabel}
              integration={integration}
            />
          ) : (
            <UnlinkedLabel primaryLabel={primaryLabel} />
          )}
        </h3>
      </div>
    </div>
  );
};
